import { action, computed, observable } from 'mobx';

import { ISocialMatchedUserState, IUserState, UserStatusKey } from '~api/RampPgApiSdk';
import { OtpType } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';

import { otpTypeMap, UserIdType } from './Config';
import { IUserFormValues } from './UserFormStore';

export type IdentifierChangedKeyType = UserIdType.EMAIL_ADDRESS | UserIdType.MOBILE_NUMBER;

export type IdentifierChangedType = {
    [key in IdentifierChangedKeyType]: boolean;
};

export class ConsentForm {
    @observable public provider: Omit<SignInType, 'guest'>;
    @observable public isConsentFlow: boolean = false;
    @observable public users: ISocialMatchedUserState[] = [];
    @observable public callbackCode: string;
    @observable public callbackState: string;
    @observable public isIdentityValueChanged: IdentifierChangedType = {
        [UserIdType.EMAIL_ADDRESS]: false,
        [UserIdType.MOBILE_NUMBER]: false
    };
    // TODO APACRIGEL-2052 use this to determine the OTP/verified logic
    @observable public verifiedIdentifiers: UserIdType[] = [];
    @observable public formFilled: boolean = false;
    @observable public isSocialSignUpFlow: boolean = false;
    // the backup form values for elc consent form which will be used in update user api after sign in to connect a complete user
    @observable public formValuesBackup: IUserFormValues | undefined;

    @action
    public setProvider = (provider: Omit<SignInType, 'guest'>) => (this.provider = provider);

    @action
    public setIsConsentFlow = (isConsentFlow: boolean) => {
        this.isConsentFlow = isConsentFlow;
    };

    @action
    public setIsIdentityValueChanged = (identifier: IdentifierChangedKeyType) => {
        this.isIdentityValueChanged[identifier] = true;
    };

    @action
    public setUsers = (users: ISocialMatchedUserState[]) => {
        this.users = users;
        this.verifiedIdentifiers = users
            .filter((user) => user.isVerified)
            .map(({ userIdType }) => userIdType);
    };

    @action
    public setCallbackCode = (code: string) => {
        this.callbackCode = code;
    };

    @action
    public setCallbackState = (state: string) => {
        this.callbackState = state;
    };

    @action
    public setFormFilled = (formFilled: boolean) => {
        this.formFilled = formFilled;
    };

    public setFormValuesBackup = (formValues: IUserFormValues | undefined) => {
        this.formValuesBackup = formValues;
    };

    @action
    public setIsSocialSignUpFlow = (isSocialSignUpFlow: boolean) => {
        this.isSocialSignUpFlow = isSocialSignUpFlow;
    };

    public isVerified = (otpType: OtpType | undefined, identifiers: UserIdType[]): boolean => {
        // TODO: APACRIGEL-2052 add comments and refine logic
        const userIdTypes = otpType ? otpTypeMap.get(otpType) : identifiers;

        return this.users.some(
            ({ userIdType, isVerified }) =>
                isVerified && userIdTypes?.includes(userIdType as UserIdType)
        );
    };

    // Check if the identifier which matched to existing user account is verified by social
    public isSocialProfileVerified = (existingUser: IUserState) => {
        return this.users.some((user) => user.isVerified && user.userId === existingUser.userId);
    };

    @computed
    public get isNewUser() {
        // TODO: APACRIGEL-2052 better to check with userId
        return this.users.every(({ userStatusKey }) =>
            [
                UserStatusKey.NEW,
                UserStatusKey.NEW_COMPLETE,
                UserStatusKey.NEW_PARTIAL,
                UserStatusKey.NEW_PARTIAL_OFFLINE
            ].includes(userStatusKey)
        );
    }

    @computed
    public get isSingleUser() {
        return (
            [...new Set(this.users.filter((user) => user.userId).map((user) => user.userId))]
                .length === 1
        );
    }

    @computed
    public get matchedOnlyOneCompleteAccount() {
        return (
            this.users.filter(
                ({ userStatusKey, userId }) =>
                    userId &&
                    [
                        UserStatusKey.EXISTING_COMPLETE,
                        UserStatusKey.EXISTING_COMPLETE_OFFLINE
                    ].includes(userStatusKey)
            )?.length === 1
        );
    }

    @computed
    public get firstCompleteAccount() {
        return this.findUser({
            status: [UserStatusKey.EXISTING_COMPLETE, UserStatusKey.EXISTING_COMPLETE_OFFLINE]
        });
    }

    @computed
    public get hasCompleteEmailAccount() {
        return this.users.some(
            ({ userStatusKey, userIdType, userId }) =>
                [UserStatusKey.EXISTING_COMPLETE, UserStatusKey.EXISTING_COMPLETE_OFFLINE].includes(
                    userStatusKey
                ) &&
                userIdType === UserIdType.EMAIL_ADDRESS &&
                userId !== null
        );
    }

    @action.bound
    public getFirstCompleteMobileAccount() {
        return this.users.find(
            ({ userStatusKey, userIdType, userId }) =>
                [UserStatusKey.EXISTING_COMPLETE, UserStatusKey.EXISTING_COMPLETE_OFFLINE].includes(
                    userStatusKey
                ) &&
                userIdType === UserIdType.MOBILE_NUMBER &&
                userId !== null
        );
    }

    @computed
    public get firstCompleteMobileAccount() {
        return this.findUser({
            status: [UserStatusKey.EXISTING_COMPLETE_OFFLINE, UserStatusKey.EXISTING_COMPLETE],
            idType: UserIdType.MOBILE_NUMBER
        });
    }

    @computed
    public get firstPartialEmailAccount() {
        return this.findUser({
            status: [UserStatusKey.EXISTING_PARTIAL_OFFLINE, UserStatusKey.EXISTING_PARTIAL],
            idType: UserIdType.EMAIL_ADDRESS
        });
    }

    @computed
    private get firstCompleteEmailAccount() {
        return this.findUser({
            status: [UserStatusKey.EXISTING_COMPLETE, UserStatusKey.EXISTING_COMPLETE_OFFLINE],
            idType: UserIdType.EMAIL_ADDRESS
        });
    }

    private findUser = ({ status, idType }: { status: UserStatusKey[]; idType?: UserIdType }) => {
        return this.users.find(
            ({ userStatusKey, userIdType, userId }) =>
                status.includes(userStatusKey) &&
                (idType === undefined || userIdType === idType) &&
                userId !== null
        );
    };
    private hasUser = ({ status, idType }: { status: UserStatusKey[]; idType?: UserIdType }) => {
        return this.users.some(
            ({ userStatusKey, userIdType, userId }) =>
                status.includes(userStatusKey) &&
                (idType === undefined || userIdType === idType) &&
                userId !== null
        );
    };

    @computed
    public get hasCompletedEmailAccount() {
        return this.hasUser({
            status: [UserStatusKey.EXISTING_COMPLETE, UserStatusKey.EXISTING_COMPLETE_OFFLINE],
            idType: UserIdType.EMAIL_ADDRESS
        });
    }

    @computed
    public get noIdentifierConflict() {
        // TODO: APACRIGEL-2052 there is no such field in post auth response
        // return this.users.every((user) => !user.identifierConflict);
        return true;
    }

    @computed
    public get hasCompleteMobileAndPartialEmail() {
        return !!this.firstCompleteMobileAccount && !!this.firstPartialEmailAccount;
    }

    @computed
    public get hasSingleCompleteMobileButNotCompleteEmail() {
        return (
            this.isSingleUser &&
            !!this.firstCompleteMobileAccount &&
            !this.firstCompleteEmailAccount
        );
    }
}
